export const COUNT_SHADING_MINIMUM_MAXIMUM = 25;
export const COUNT_SHADING_MAXIMUM_INCREMENT = 25;

export const DEFAULT_COUNT_SHADING_THRESHOLDS = Object.freeze({
  lowerCount: 2,
  upperCount: 5,
});

export const COUNT_SHADING_FILL_STYLE = Object.freeze({
  belowLowerColor: "#FFF3B0",
  middleColor: "#F59E0B",
  atOrAboveUpperColor: "#DC2626",
  opacity: 0.72,
});

export function countShadingMaximum(
  counts,
  minimum = COUNT_SHADING_MINIMUM_MAXIMUM,
  increment = COUNT_SHADING_MAXIMUM_INCREMENT,
) {
  if (!Array.isArray(counts)) throw new TypeError("Expected an array of sick-report counts.");
  if (!Number.isInteger(minimum) || minimum < 1) {
    throw new RangeError("The minimum slider maximum must be a positive whole number.");
  }
  if (!Number.isInteger(increment) || increment < 1) {
    throw new RangeError("The slider maximum increment must be a positive whole number.");
  }
  const maximumCount = counts.reduce((maximum, count) => {
    if (!Number.isInteger(count) || count < 0) {
      throw new TypeError("Sick-report counts must be non-negative whole numbers.");
    }
    return Math.max(maximum, count);
  }, 0);
  return Math.max(minimum, Math.ceil(maximumCount / increment) * increment);
}

export function validateCountShadingThresholds(
  thresholds,
  maximum = Number.MAX_SAFE_INTEGER,
) {
  validateMaximum(maximum);
  const lowerCount = thresholds?.lowerCount;
  const upperCount = thresholds?.upperCount;
  if (!Number.isInteger(lowerCount) || !Number.isInteger(upperCount)) {
    throw new TypeError("Sick-report count thresholds must be whole numbers.");
  }
  if (lowerCount < 0 || lowerCount > maximum || upperCount < 0 || upperCount > maximum) {
    throw new RangeError(`Sick-report count thresholds must be between 0 and ${maximum}.`);
  }
  if (lowerCount >= upperCount) {
    throw new RangeError("The lower sick-report count threshold must be lower than the upper threshold.");
  }
  return Object.freeze({ lowerCount, upperCount });
}

export function countShadingSliderBounds(
  thresholds,
  maximum = COUNT_SHADING_MINIMUM_MAXIMUM,
) {
  const { lowerCount, upperCount } = validateCountShadingThresholds(thresholds, maximum);
  return Object.freeze({
    lowerMinimum: 0,
    lowerMaximum: upperCount - 1,
    upperMinimum: lowerCount + 1,
    upperMaximum: maximum,
  });
}

export function countForPointerPosition(
  clientX,
  { left, width },
  maximum = COUNT_SHADING_MINIMUM_MAXIMUM,
) {
  validateMaximum(maximum);
  if (!Number.isFinite(width) || width <= 0) return 0;
  const fraction = Math.max(0, Math.min(1, (clientX - left) / width));
  return Math.round(fraction * maximum);
}

export function closestCountShadingHandle(count, thresholds) {
  const { lowerCount, upperCount } = validateCountShadingThresholds(thresholds);
  return Math.abs(count - lowerCount) <= Math.abs(count - upperCount)
    ? "lowerCount"
    : "upperCount";
}

export function clampCountShadingThreshold(
  name,
  count,
  thresholds,
  maximum = COUNT_SHADING_MINIMUM_MAXIMUM,
) {
  const bounds = countShadingSliderBounds(thresholds, maximum);
  const numericCount = numericThresholdValue(count);
  if (!Number.isFinite(numericCount)) {
    if (name === "lowerCount") return thresholds.lowerCount;
    if (name === "upperCount") return thresholds.upperCount;
    throw new TypeError(`Unknown sick-report count threshold: ${name}.`);
  }
  const value = Math.round(numericCount);
  if (name === "lowerCount") {
    return Math.max(bounds.lowerMinimum, Math.min(bounds.lowerMaximum, value));
  }
  if (name === "upperCount") {
    return Math.max(bounds.upperMinimum, Math.min(bounds.upperMaximum, value));
  }
  throw new TypeError(`Unknown sick-report count threshold: ${name}.`);
}

export function nextCountShadingThresholds(
  name,
  count,
  thresholds,
  maximum = COUNT_SHADING_MINIMUM_MAXIMUM,
) {
  return validateCountShadingThresholds({
    ...thresholds,
    [name]: clampCountShadingThreshold(name, count, thresholds, maximum),
  }, maximum);
}

export function normalizeCountShadingThresholds(
  thresholds,
  maximum,
  defaults = DEFAULT_COUNT_SHADING_THRESHOLDS,
) {
  validateMaximum(maximum);
  const defaultUpper = roundedThresholdOrFallback(defaults?.upperCount, 1);
  const defaultLower = roundedThresholdOrFallback(defaults?.lowerCount, 0);
  const oldUpper = roundedThresholdOrFallback(thresholds?.upperCount, defaultUpper);
  const oldLower = roundedThresholdOrFallback(thresholds?.lowerCount, defaultLower);
  const upperCount = Math.max(1, Math.min(maximum, oldUpper));
  const lowerCount = Math.max(0, Math.min(upperCount - 1, oldLower));
  return validateCountShadingThresholds({ lowerCount, upperCount }, maximum);
}

export function sickReportCountCategory(
  sickCount,
  thresholds = DEFAULT_COUNT_SHADING_THRESHOLDS,
) {
  const { lowerCount, upperCount } = validateCountShadingThresholds(thresholds);
  if (!Number.isInteger(sickCount) || sickCount < 0) {
    throw new RangeError("Sick-report count must be a non-negative whole number.");
  }
  if (sickCount < lowerCount) return "belowLower";
  if (sickCount < upperCount) return "middle";
  return "atOrAboveUpper";
}

export function sickReportCountColor(
  sickCount,
  thresholds = DEFAULT_COUNT_SHADING_THRESHOLDS,
) {
  const category = sickReportCountCategory(sickCount, thresholds);
  if (category === "belowLower") return COUNT_SHADING_FILL_STYLE.belowLowerColor;
  if (category === "middle") return COUNT_SHADING_FILL_STYLE.middleColor;
  return COUNT_SHADING_FILL_STYLE.atOrAboveUpperColor;
}

export function countShadingFillColorExpression(
  thresholds = DEFAULT_COUNT_SHADING_THRESHOLDS,
) {
  const { lowerCount, upperCount } = validateCountShadingThresholds(thresholds);
  return [
    "step",
    ["get", "sickCount"],
    COUNT_SHADING_FILL_STYLE.belowLowerColor,
    lowerCount,
    COUNT_SHADING_FILL_STYLE.middleColor,
    upperCount,
    COUNT_SHADING_FILL_STYLE.atOrAboveUpperColor,
  ];
}

function validateMaximum(maximum) {
  if (!Number.isInteger(maximum) || maximum < 1) {
    throw new RangeError("The sick-report count slider maximum must be a positive whole number.");
  }
}

function roundedThresholdOrFallback(value, fallback) {
  const numericValue = numericThresholdValue(value);
  return Number.isFinite(numericValue) ? Math.round(numericValue) : fallback;
}

function numericThresholdValue(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") return Number(value);
  return Number.NaN;
}
